import { useEffect, useRef, useState, type ReactNode } from 'react';
import { uniformDashboardAPI } from '../../services/api';
import { Modal } from './DashboardPrimitives';
import { InlineLoader } from '../loading';
import {
  classifyFailure,
  fmtScanTime,
  uniqueAwbs,
  type UniformAwb,
  type UniformLoadFailure,
  type UniformTimeline,
} from './uniformDashboard';

/**
 * Dashboard → Uniform & Asset Management, Courier In Process: where is the parcel.
 *
 * It reads the courier module's own data through 1Office Admin Management — the
 * shipments booked on the request, then one shipment's tracking history — so the
 * status here can never differ from Admin Management's Courier → In Process. The API
 * answers only for a request on this project, and only for an AWB booked on that
 * request, so neither id can be used to look at someone else's parcel.
 *
 * Three things that are easy to get wrong:
 *   • A uniform kit ships as ONE parcel under one AWB, so the AWBs are
 *     de-duplicated, and the switcher appears only if a request somehow holds more
 *     than one.
 *   • The two calls fail apart. The tracking history is asked of the courier vendor
 *     at the moment the modal opens; when that fails, the booking status the courier
 *     sync last stored is still shown, with the failure said underneath — a booked
 *     parcel must not look unbooked just because its history could not be fetched.
 *   • A 403 from either call is the exception to that. It is the access answer for
 *     the whole tab (the grant was withdrawn while the tab was open), so it goes to
 *     `onDenied` — the tab closes this modal, clears the project's rows and shows
 *     its access panel — rather than being printed here over a receiver's name and
 *     destination the reader may no longer see.
 */

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center gap-2.5 py-0.5">
      <span className="min-w-[90px] whitespace-nowrap text-[10px] font-semibold uppercase tracking-wider text-gray-500">{label}</span>
      <span className="text-[13px] font-semibold text-gray-900">{children}</span>
    </div>
  );
}

export default function UniformCourierStatusModal({
  projectId,
  requestId,
  requestCode,
  onClose,
  onDenied,
}: {
  projectId: number;
  requestId: number;
  requestCode: string;
  onClose: () => void;
  /** The API answered 403: the tab's access answer, with the server's message. */
  onDenied: (failure: Extract<UniformLoadFailure, { kind: 'denied' }>) => void;
}) {
  const [awbs, setAwbs] = useState<UniformAwb[] | null>(null);
  const [awbError, setAwbError] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  // Keyed by AWB, so switching parcels never shows the previous one's scans — or its
  // error — while the next loads.
  const [loaded, setLoaded] = useState<{ awb: string; data: UniformTimeline } | null>(null);
  const [failed, setFailed] = useState<{ awb: string; message: string } | null>(null);
  // Through a ref, so neither load below runs again each time the tab re-renders and
  // hands over a new function.
  const onDeniedRef = useRef(onDenied);
  onDeniedRef.current = onDenied;

  useEffect(() => {
    let live = true;
    setAwbs(null);
    setAwbError(null);
    setSelected(null);
    uniformDashboardAPI
      .getAwbs({ projectId, requestId })
      .then((res) => {
        if (!live) return;
        const unique = uniqueAwbs(res.data?.data?.awbs);
        setAwbs(unique);
        setSelected(unique[0]?.awb_code ?? null);
      })
      .catch((err) => {
        if (!live) return;
        const failure = classifyFailure(err, 'Failed to load the booking.');
        if (failure.kind === 'denied') {
          onDeniedRef.current(failure);
          return;
        }
        setAwbs([]);
        setAwbError(failure.message);
      });
    return () => { live = false; };
  }, [projectId, requestId]);

  useEffect(() => {
    if (!selected) return undefined;
    let live = true;
    uniformDashboardAPI
      .getTimeline({ projectId, requestId, awb: selected })
      .then((res) => {
        if (live) setLoaded({ awb: selected, data: (res.data?.data ?? {}) as UniformTimeline });
      })
      .catch((err) => {
        if (!live) return;
        const failure = classifyFailure(err, 'Failed to load the tracking timeline.');
        if (failure.kind === 'denied') onDeniedRef.current(failure);
        else setFailed({ awb: selected, message: failure.message });
      });
    return () => { live = false; };
  }, [selected, projectId, requestId]);

  const awb = awbs?.find((a) => a.awb_code === selected) ?? null;
  const timeline = loaded && loaded.awb === selected ? loaded.data : null;
  const timelineError = !timeline && failed && failed.awb === selected ? failed.message : null;
  const timelineLoading = !!selected && !timeline && !timelineError;
  const scans = Array.isArray(timeline?.timeline) ? timeline.timeline : [];
  // The vendor's live status when the timeline loaded; otherwise the status the
  // courier sync last stored on the booking.
  const currentStatus = timeline?.current_status || awb?.booking_status || '—';

  return (
    <Modal title={`Courier Status (${requestCode})`} onClose={onClose} maxWidth="max-w-3xl">
      {awbs === null ? (
        <p className="py-10 text-center text-sm text-gray-500"><InlineLoader label="Loading…" className="flex justify-center" /></p>
      ) : awbError ? (
        <p className="py-10 text-center text-sm text-red-600" role="alert">{awbError}</p>
      ) : !awb ? (
        <p className="py-10 text-center text-sm text-gray-500">
          No AWB on this request yet, so there is no courier status to show.
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {awbs.length > 1 && (
            <div className="flex flex-wrap gap-2" role="group" aria-label="Parcels on this request">
              {awbs.map((a) => {
                const on = a.awb_code === selected;
                return (
                  <button
                    key={a.awb_code}
                    type="button"
                    aria-pressed={on}
                    onClick={() => setSelected(a.awb_code)}
                    className={`rounded-full border px-3 py-1 font-mono text-xs font-medium transition ${
                      on ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {a.awb_code}
                  </button>
                );
              })}
            </div>
          )}

          <div className="grid grid-cols-1 gap-x-6 border-b border-gray-100 pb-3 sm:grid-cols-2">
            <Field label="AWB Code"><span className="font-mono text-primary-600">{awb.awb_code}</span></Field>
            <Field label="Courier">{timeline?.courier_name || awb.courier_name || '—'}</Field>
            <Field label="Status">
              <span className="inline-flex items-center rounded-full bg-primary-50 px-2.5 py-0.5 text-[11px] font-semibold text-primary-700">
                {currentStatus}
              </span>
            </Field>
            <Field label="Receiver">{awb.receiver_name || '—'}</Field>
            <Field label="Destination">{[awb.destination_city, awb.pincode].filter(Boolean).join(' · ') || '—'}</Field>
          </div>

          <div>
            <h4 className="mb-3 text-[13px] font-bold uppercase tracking-wider text-gray-700">Tracking Timeline</h4>
            {timelineLoading ? (
              <p className="py-6 text-center text-sm text-gray-500"><InlineLoader label="Loading…" className="flex justify-center" /></p>
            ) : timelineError ? (
              <p className="py-6 text-center text-sm text-red-600" role="alert">{timelineError}</p>
            ) : scans.length > 0 ? (
              // Newest first, as the courier sends it — the list is not re-sorted, so
              // the green dot on the first entry is the parcel's latest scan.
              <ol className="relative ml-2 border-l border-gray-200 pl-4">
                {scans.map((entry, idx) => (
                  <li key={`${entry.datetime}-${idx}`} className="mb-5 ml-2">
                    <span className={`absolute -left-2 flex h-4 w-4 rounded-full ring-4 ring-white ${idx === 0 ? 'bg-emerald-500' : 'bg-primary-500'}`} />
                    <time className="mb-1 block text-xs text-gray-400">{fmtScanTime(entry.datetime)}</time>
                    <p className="text-sm font-semibold text-gray-900">{entry.status}</p>
                    {entry.activity && <p className="text-xs text-gray-600">{entry.activity}</p>}
                    {entry.location && <p className="text-xs text-gray-500">{entry.location}</p>}
                  </li>
                ))}
              </ol>
            ) : (
              <p className="py-6 text-center text-sm text-gray-500">No tracking activity yet.</p>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
